import {
  CloseRounded,
  QrCodeRounded,
  KeyboardRounded,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AddProduct, UpdateProduct } from "../../../../../api/inventory";
import { ErrorCodes } from "../../../../../types/enums";
import {
  Status,
  Product,
  InventoryData,
} from "../../../../../types/inventoryData";
import { useApplicationContext } from "../../../../ContextProviders/ApplicationContext";
import { useInventory } from "../../../InventoryProvider";
import QrScanner from "../../QrScanner";
import AddProductManuallyBatchScan from "./AddProductManuallyBatchScan";
import { Controller, useForm } from "react-hook-form";
import { inventoryRules } from "../../../../../helpers/validationSchemes";

type AddItemPopupTypes = {
  open: boolean;
  handleClose: () => void;
};

type PanelProps = {
  children?: React.ReactNode;
  index: number;
  value: number;
};

const CustomTabPanel: React.FC<PanelProps> = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel${index}`}
      style={{ width: "100%", height: "100%", overflow: "hidden" }}
    >
      {value === index && children}
    </div>
  );
};

const BatchScan: React.FC<AddItemPopupTypes> = ({ open, handleClose }) => {
  const {
    rows,
    isPhoneSize,
    setEditingRow,
    updateSingleRow,
    fetchPlans,
    addBatchNumberAndActions,
  } = useInventory();
  const { showSnackbar } = useApplicationContext();
  const { t } = useTranslation();
  const [chosenValues, setChosenValues] = useState<InventoryData | null>(null);
  const [step, setStep] = useState<number>(0);
  const [tabValue, setTabValue] = useState(0);
  const { handleSubmit, control, reset } = useForm<InventoryData>({
    defaultValues: {
      serial: "",
    },
    mode: "onChange",
  });

  const handleSave = async (serialNumber: string) => {
    const foundProduct = rows.find((r) => r.serial == serialNumber);
    if (foundProduct == undefined) {
      showSnackbar(t("errors.ProductNotFound"), "error");
    } else if (foundProduct != undefined && chosenValues != null) {
      if (chosenValues.plan == null) foundProduct.plan = null;

      const changedObject: InventoryData = {
        ...foundProduct,
        ...chosenValues,
      };

      try {
        const updatedProduct: InventoryData = await UpdateProduct(
          changedObject
        );
        const newUpdatedProduct = addBatchNumberAndActions(updatedProduct);

        updateSingleRow(newUpdatedProduct);
        fetchPlans();

        setEditingRow(null);
        showSnackbar(
          `${updatedProduct.serial} ${t("inventory.successupdate")}`,
          "success"
        );
      } catch (error) {
        if (error instanceof Error) {
          switch (error.message) {
            case ErrorCodes.ProductNotFound:
              showSnackbar(t("errors.ProductNotFound"), "error");
              break;
            default:
              showSnackbar(t("errors.UnexpectedError"), "error");
              break;
          }
        } else {
          showSnackbar(t("errors.UnexpectedError"), "error");
        }
      }
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isPhoneSize}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            // width: 600,
          }}
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              px: 3,
              py: 2,
            }}
          >
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <DialogTitle sx={{ p: 0 }}>
                {t("inventory.batchEditTitleWoNumbers")}
              </DialogTitle>
              <IconButton
                onClick={handleClose}
                sx={{
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  "&:hover": { cursor: "pointer" },
                }}
              >
                <CloseRounded />
              </IconButton>
            </Box>
            <Typography variant="body1">{t("inventory.batchFeature")}</Typography>
          </Box>

          {step == 0 && (
            <AddProductManuallyBatchScan
              chosenValues={chosenValues}
              handleAdd={(data, isDirty, reset) => {
                if (isDirty) {
                  setChosenValues(data);
                  setStep(1);
                }
              }}
            />
          )}

          {step == 1 && (
            <>
              <Box
                sx={{
                  width: "100%",
                }}
              >
                <Tabs
                  value={tabValue}
                  onChange={(_, number) => setTabValue(number)}
                  selectionFollowsFocus
                  variant="fullWidth"
                >
                  <Tab icon={<QrCodeRounded />} label="Scan" />
                  <Tab icon={<KeyboardRounded />} label="Manual" />
                </Tabs>
              </Box>

              <CustomTabPanel value={tabValue} index={0}>
                <QrScanner
                  handleScan={(res) => {
                    handleSave(res[0].rawValue);
                    reset;
                  }}
                />
              </CustomTabPanel>
              <CustomTabPanel value={tabValue} index={1}>
                <Box p={3} width={"100%"}>
                  <form
                    onSubmit={handleSubmit((data) => handleSave(data.serial))}
                  >
                    <Controller
                      name="serial"
                      control={control}
                      rules={inventoryRules.serialRule}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          fullWidth
                          error={!!error}
                          id="serial"
                          name="serial"
                          required
                          variant="filled"
                          label={t("labels.serial")}
                          helperText={
                            <>
                              {/* Counter */}
                              {`${field?.value?.length || 0}/${
                                inventoryRules.serialRuleMaxLength
                              }`}

                              {/* Error */}
                              {error?.message &&
                                `. ${t(error.message).replace(
                                  "{0}",
                                  inventoryRules.serialRuleMaxLength.toString()
                                )}`}
                            </>
                          }
                          onKeyDown={(event) =>
                            handleSubmit((data) => {
                              handleSave(data.serial);
                              reset;
                            })
                          }
                        />
                      )}
                    />
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "flex-end",
                      }}
                    >
                      <Button type="submit" variant="contained">
                        Change
                      </Button>
                    </Box>
                  </form>
                </Box>
              </CustomTabPanel>

              <Box
                sx={{
                  display: "flex",
                  pl: 3,
                  pr: 3,
                  pt: 2,
                  pb: 2,
                  justifyContent: "space-between",
                  gap: 2,
                  width: "100%",
                }}
              >
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={() => setStep(0)}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={handleClose}
                >
                  Done
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Dialog>
    </>
  );
};

export default BatchScan;
